import { useEffect, useState, useCallback, useMemo } from 'react';
import { getSalesComparison, SalesComparison } from './salesComparisonApi';

export type Period = 'week' | 'month';
export type ReceiptMode = 'all' | 'pos';

function isoWeek(d: Date): number {
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function currentIsoWeek(): number { return isoWeek(new Date()); }
function currentMonth(): number { return new Date().getMonth() + 1; }
function currentYear(): number { return new Date().getFullYear(); }

interface RouteState {
    year: number;
    period: Period;
    number: number;
    current: boolean;
    receiptMode: ReceiptMode;
}

// Resolves "current" or "previous" to a concrete year + number.
function resolveSelector(period: Period, selector: string): { year: number; number: number; current: boolean } {
    if (selector === 'current') {
        const n = period === 'week' ? currentIsoWeek() : currentMonth();
        return { year: currentYear(), number: n, current: true };
    }
    if (selector === 'previous') {
        if (period === 'week') {
            const w = currentIsoWeek();
            return { year: w === 1 ? currentYear() - 1 : currentYear(), number: w === 1 ? 52 : w - 1, current: false };
        }
        const m = currentMonth();
        return { year: m === 1 ? currentYear() - 1 : currentYear(), number: m === 1 ? 12 : m - 1, current: false };
    }
    // Numeric — explicit number, used in year-based permalink URLs
    const n = parseInt(selector, 10) || 1;
    return { year: currentYear(), number: n, current: false };
}

// Parses both URL forms:
//   #/sales/reports/week/current           (relative)
//   #/sales/reports/year/2026/week/23      (permalink)
function parseRoute(): RouteState {
    const hash = window.location.hash.replace(/^#\/?/, '');
    const [path, qs] = hash.split('?');
    const segments = path.split('/');
    const params = new URLSearchParams(qs || '');
    const receiptMode: ReceiptMode = params.get('receiptMode') === 'pos' ? 'pos' : 'all';

    const yearIdx = segments.indexOf('year');
    if (yearIdx >= 0) {
        // Permalink: .../year/2026/week/23
        const year = parseInt(segments[yearIdx + 1], 10) || currentYear();
        const period: Period = segments[yearIdx + 2] === 'month' ? 'month' : 'week';
        const number = parseInt(segments[yearIdx + 3], 10) || 1;
        const current = year === currentYear() && (
            period === 'week' ? number === currentIsoWeek() : number === currentMonth()
        );
        return { year, period, number, current, receiptMode };
    }

    // Relative: .../reports/week/current
    const reportsIdx = segments.indexOf('reports');
    const period: Period = segments[reportsIdx + 1] === 'month' ? 'month' : 'week';
    const selector = segments[reportsIdx + 2] || 'current';
    const resolved = resolveSelector(period, selector);
    return { ...resolved, period, receiptMode };
}

// Owns the report selection and (re)fetches whenever the URL changes.
// The four toggle buttons are plain <a> links that navigate the browser — the hook just reads
// the URL on mount and on hashchange.
export function useSalesComparison() {
    const initial = useMemo(() => parseRoute(), []);
    const [state, setState] = useState(initial);
    const [data, setData] = useState<SalesComparison | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Re-parse when the hash changes (button click navigates via <a href>).
    useEffect(() => {
        const onHashChange = () => setState(parseRoute());
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Receipt mode is local state (query param, not a navigation).
    const setReceiptMode = useCallback((rm: ReceiptMode) => {
        setState(s => ({ ...s, receiptMode: rm }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getSalesComparison(state.period, state.year, state.number, state.receiptMode)
            .then(result => { if (!cancelled) setData(result); })
            .catch(e => { if (!cancelled) setError(String(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [state.period, state.year, state.number, state.receiptMode]);

    return {
        period: state.period,
        isCurrent: state.current,
        receiptMode: state.receiptMode,
        setReceiptMode,
        data, loading, error,
    };
}
